import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  AlertCircle,
  Play,
  RotateCcw,
  Copy,
  Download,
  CheckCircle,
  XCircle,
  Terminal,
} from 'lucide-react';
import { fetchPlaybooks, runPlaybook } from '@/api/admin';
import { toast } from 'sonner';
import type { PlaybookRunResult, PlaybookRunStats } from '@/types/admin';

interface PlaybookResult {
  output?: string;
  success?: boolean;
  stats?: Record<string, PlaybookRunStats>;
}

export function AdminPlaybookRunnerPage() {
  const { data: playbooks, isLoading, error } = useQuery({
    queryKey: ['admin-playbooks'],
    queryFn: fetchPlaybooks,
  });

  const [form, setForm] = useState({
    hostname: '',
    username: 'root',
    auth_type: 'password' as 'password' | 'key',
    password: '',
    ssh_key: '',
    playbook: '',
    extra_vars: '{}',
  });

  const [jsonError, setJsonError] = useState<string | null>(null);
  const [result, setResult] = useState<PlaybookResult>({});

  const runMutation = useMutation({
    mutationFn: () => {
      let extraVars: Record<string, any> = {};
      try {
        extraVars = form.extra_vars ? JSON.parse(form.extra_vars) : {};
        setJsonError(null);
      } catch (e) {
        throw new Error('Extra vars JSON is invalid');
      }
      return runPlaybook({
        hostname: form.hostname,
        username: form.username,
        auth_type: form.auth_type,
        password: form.auth_type === 'password' ? form.password : undefined,
        ssh_key: form.auth_type === 'key' ? form.ssh_key : undefined,
        playbook: form.playbook,
        extra_vars: extraVars,
      });
    },
    onSuccess: (data: PlaybookRunResult) => {
      setResult({
        output: data.output,
        success: data.success,
        stats: data.stats,
      });
      toast.success(data.success ? 'Playbook executed successfully' : 'Playbook execution failed');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to run playbook'),
  });

  const validateJson = (value: string) => {
    if (!value.trim()) {
      setJsonError(null);
      return;
    }
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (e) {
      setJsonError('Invalid JSON format');
    }
  };

  const resetForm = () => {
    setForm({
      hostname: '',
      username: 'root',
      auth_type: 'password',
      password: '',
      ssh_key: '',
      playbook: '',
      extra_vars: '{}',
    });
    setResult({});
    setJsonError(null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const downloadOutput = () => {
    if (!result.output) return;
    const blob = new Blob([result.output], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `playbook-${form.playbook}-output.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Output downloaded');
  };

  const canRun = form.hostname && form.username && form.playbook && !jsonError && (
    form.auth_type === 'password' ? form.password : form.ssh_key
  );

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Playbook Runner</h1>
          <p className="text-sm text-muted-foreground">Execute Ansible playbooks against remote hosts</p>
        </div>
        <Button variant="outline" onClick={resetForm}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load playbooks.</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Terminal className="h-5 w-5" />
              Run Playbook
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Server Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hostname">Hostname / IP</Label>
                <Input
                  id="hostname"
                  placeholder="e.g., 192.168.1.100"
                  value={form.hostname}
                  onChange={(e) => setForm((f) => ({ ...f, hostname: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  placeholder="root"
                  value={form.username}
                  onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                />
              </div>
            </div>

            {/* Auth Type */}
            <div className="space-y-2">
              <Label>Authentication</Label>
              <RadioGroup
                value={form.auth_type}
                onValueChange={(v) => setForm((f) => ({ ...f, auth_type: v as 'password' | 'key' }))}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="password" id="auth-password" />
                  <Label htmlFor="auth-password" className="font-normal">Password</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="key" id="auth-key" />
                  <Label htmlFor="auth-key" className="font-normal">SSH Key</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Password or SSH Key */}
            {form.auth_type === 'password' ? (
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="SSH password"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="ssh-key">SSH Private Key</Label>
                <Textarea
                  id="ssh-key"
                  placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                  value={form.ssh_key}
                  onChange={(e) => setForm((f) => ({ ...f, ssh_key: e.target.value }))}
                  rows={4}
                  className="font-mono text-xs"
                />
              </div>
            )}

            {/* Playbook Selection */}
            <div className="space-y-2">
              <Label htmlFor="playbook">Playbook</Label>
              <Input
                id="playbook"
                placeholder="Select or type playbook name"
                list="playbook-list"
                value={form.playbook}
                onChange={(e) => setForm((f) => ({ ...f, playbook: e.target.value }))}
              />
              <datalist id="playbook-list">
                {playbooks?.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
              {isLoading && <p className="text-xs text-muted-foreground">Loading playbook list...</p>}
            </div>

            {/* Extra Vars */}
            <div className="space-y-2">
              <Label htmlFor="extra-vars">Extra Variables (JSON)</Label>
              <Textarea
                id="extra-vars"
                placeholder='{"key": "value"}'
                value={form.extra_vars}
                onChange={(e) => {
                  setForm((f) => ({ ...f, extra_vars: e.target.value }));
                  validateJson(e.target.value);
                }}
                rows={4}
                className={`font-mono text-xs ${jsonError ? 'border-red-500' : ''}`}
              />
              {jsonError && (
                <p className="text-xs text-red-500">{jsonError}</p>
              )}
            </div>

            {/* Run Button */}
            <Button
              onClick={() => runMutation.mutate()}
              disabled={runMutation.isPending || !canRun}
              className="w-full"
            >
              <Play className="h-4 w-4 mr-2" />
              {runMutation.isPending ? 'Running...' : 'Run Playbook'}
            </Button>
          </CardContent>
        </Card>

        {/* Result Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Execution Result</CardTitle>
              {result.output && (
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(result.output!)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={downloadOutput}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!result.output ? (
              <div className="text-center py-12 text-muted-foreground">
                <Terminal className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Run a playbook to see results here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Status */}
                <div className="flex items-center gap-2">
                  {result.success ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Success</Badge>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 text-red-600" />
                      <Badge variant="destructive">Failed</Badge>
                    </>
                  )}
                </div>

                {/* Per-host stats */}
                {result.stats && Object.keys(result.stats).length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Host Statistics</Label>
                    <div className="bg-muted rounded-lg p-3">
                      {Object.entries(result.stats).map(([host, stats]) => (
                        <div key={host} className="space-y-1">
                          <p className="text-sm font-mono">{host}</p>
                          <div className="flex flex-wrap gap-2 text-xs">
                            <Badge variant="outline" className="bg-green-50">
                              OK: {stats.ok || 0}
                            </Badge>
                            <Badge variant="outline" className="bg-yellow-50">
                              Changed: {stats.changed || 0}
                            </Badge>
                            <Badge variant="outline" className="bg-red-50">
                              Failures: {stats.failures || 0}
                            </Badge>
                            <Badge variant="outline" className="bg-gray-50">
                              Unreachable: {stats.unreachable || 0}
                            </Badge>
                            <Badge variant="outline" className="bg-blue-50">
                              Skipped: {stats.skipped || 0}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Output */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Output</Label>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto max-h-80 whitespace-pre-wrap">
                    {result.output}
                  </pre>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
